import { BaseViewStore } from "core/stores";
import _ from 'lodash';
import { FacilityInventoryCountDetailService, FacilityInventoryCountHeaderService } from "application/facility-inventory/services";
import { FacilityOrderService, FacilityInventoryCalculationService } from "application/facility-order/services";
import { toUTCDate } from 'core/utils';
import { action, observable } from 'mobx';
import { QueryUtility } from 'core/utils';
import { applicationContext } from "core/utils";
import { ModalParams } from 'core/models';
import { ProductService } from 'application/product/services';

@applicationContext
class FacilityInventoryCountDetailListViewStore extends BaseViewStore {
	@observable accessor counter = 0;
	@observable accessor downloadUrl = null;
	@observable accessor newDownloadUrl = null;
	@observable accessor facilityInventoryCountDetails = [];
	@observable accessor oldQuantity = [];
	@observable accessor isDone = false;
	@observable accessor loadingData = false;
	constructor(rootStore) {
        super(rootStore);

        this.facilityInventoryCountDetailService = new FacilityInventoryCountDetailService(rootStore.application.baasic.apiClient);
        this.facilityInventoryCountHeaderService = new FacilityInventoryCountHeaderService(rootStore.application.baasic.apiClient);
        this.facilityOrderService = new FacilityOrderService(rootStore.application.baasic.apiClient);
        this.facilityInventoryCalculationService = new FacilityInventoryCalculationService(rootStore.application.baasic.apiClient);
        this.productService = new ProductService(rootStore.application.baasic.apiClient);
        this.facilityInventoryCountHeaderId = rootStore.routerStore.router.routerState.params.facilityInventoryCountHeaderId;

        this.isDone = rootStore.routerStore.router.routerState.params.isDone;

        this.facilityInventoryCountDetailsRowRefs = [];
        this.hasPermission = (authorization) => {
			return this.rootStore.permissionStore.hasPermission(authorization);
		};
        this.isCreate = !this.isDone;
        this.authorization = {
			create: this.hasPermission('facilityOrderSection.create'),
			update: this.hasPermission('facilityOrderSection.update'),
			delete: this.hasPermission('facilityOrderSection.delete')
		};
        this.queryUtility = new QueryUtility(this.rootStore, async filter => {
			const responseData = await this.getProducts(filter);

			var caseResponseData = null;
			if (responseData.item.length > 0) {
				var caseProducts = responseData.item.map(e => e.itemId.toLowerCase().replace("-n", "-cs-n").replace("-b", "-cs-b"));
				if (caseProducts.filter(p => p.indexOf("-cs") > -1).length > 0) {
					caseResponseData = (await this.productService.find({ itemIds: caseProducts.filter(p => p.indexOf("-cs") > -1).join(','), isActive: true, rpp: 1000 })).data.item;
				}
			}

			this.facilityInventoryCountDetails = _.map(responseData.item, (item => {
				var product = caseResponseData && caseResponseData.find(x => x.itemId.toLowerCase().replace("-cs-n", "-n").replace("-cs-b", "-b") === item.itemId.toLowerCase());
				return _.extend(
					item,
					product && product.qtyPerCase && product.qtyPerCase > 0 ?
						{ caseQty: item.quantity > 0 ? Math.floor(item.quantity / product.qtyPerCase) : 0, eachQty: item.quantity > 0 ? (item.quantity % product.qtyPerCase) : 0, qtyPerCase: product.qtyPerCase } :
						{ caseQty: 0, eachQty: item.quantity, qtyPerCase: 0 }
				);
			}));

			this.oldQuantity = _.map(responseData.item, (item => { return { caseQty: item.caseQty, eachQty: item.eachQty } }));
		}, {});

        this.editModal = new ModalParams({
			onClose: () => {
				this.setTitle();
			}
		});

        if (rootStore.routerStore.router.routerState.params.facilityInventoryCountHeaderId) {
			this.fetch([this.queryUtility.initialize()]);
		}
        this.downloadUrl = `${this.facilityInventoryCountDetailService.getDownloadInventoryDetailUrl({
			...this.queryUtility.plainFilter,
			facilityInventoryCountHeaderId: this.facilityInventoryCountHeaderId
		})}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`

        this.newDownloadUrl = `${this.facilityInventoryCountDetailService.getNewDownloadInventoryDetailUrl({
			...this.queryUtility.plainFilter,
			facilityInventoryCountHeaderId: this.facilityInventoryCountHeaderId
		})}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`

        fetch([this.find()]);
    }

	@action.bound
	async find() {
		this.loadingData = true;
		let response = await this.facilityOrderService.find({ facilityInventoryCountHeaderId: this.facilityInventoryCountHeaderId });
		this.facilityOrder = _.first(response.data.item);
		if (this.facilityOrder) {
			const facilityInventoryCalculationResponse = await this.facilityInventoryCalculationService.getExisting(this.facilityOrder.id);
			if (facilityInventoryCalculationResponse && facilityInventoryCalculationResponse.isDone) {
				this.isDone = true;
			}
		}
		
		this.setTitle();
		this.loadingData = false;
	}

	async setTitle() {
		const facilityInventoryCountHeaderService = new FacilityInventoryCountHeaderService(
			this.rootStore.application.baasic.apiClient
		);
		const facilityInventoryCountHeaderId = this.rootStore.routerStore.routerState.params.facilityInventoryCountHeaderId;
		const facilityInventoryCountHeaderResponse = await facilityInventoryCountHeaderService.get(
			facilityInventoryCountHeaderId,
			{ embed: "Facility" }
		);
		const facilityInventoryCountHeader =
			facilityInventoryCountHeaderResponse.data.facility &&
			facilityInventoryCountHeaderResponse.data.facility.name;
		const facilityInventoryCountHeaderTitle = `${facilityInventoryCountHeader} - Count Date [${toUTCDate(
			facilityInventoryCountHeaderResponse.data.countDate
		)}]`;

		this.rootStore.titleStore.setTitle(facilityInventoryCountHeader ? `FI - ${facilityInventoryCountHeader}` : 'Facility Inventory');
		this.rootStore.viewStore.setNavigationOptions({
			title: facilityInventoryCountHeaderTitle,
			openModal: this.isDone ? null : this.openEditModal
		});
	}

	@action.bound
	async onCreate() {
		await this.queryUtility.fetch();
	}

	@action.bound
	async getProducts(filter) {
		var params = _.extend(filter);
		if (this.counter == 0) {
			//set default rpp to 50
			this.counter++;
			this.queryUtility.filter.reset(50);
			params = this.queryUtility.plainFilter;
		}
		const response = await this.facilityInventoryCountDetailService.find(
			_.extend(params, { facilityInventoryCountHeaderId: this.facilityInventoryCountHeaderId, embed: 'Product', sort: 'ItemType|asc' })
		);
		const responseData = _.extend(response.data, {
			item: _.map(response.data.item, (item => {
				return _.extend(
					item, {
					itemId: item.product && item.product.itemId,
					description: item.product && item.product.description,
					procedureCode: item.product && item.product.procedureCode,
					quantity: item.quantity,
					uoM: item.product && item.product.uoM
				}
				);
			}))
		});
		return responseData;
	}

	@action.bound
	async deleteResource(item) {
		const { modalStore, notificationStore } = this.rootStore;

		modalStore.showConfirm(
			'FACILITY_INVENTORY.DELETE.CONFIRM',
			async () => {
				try {
					await this.facilityInventoryCountDetailService.delete(item);
					notificationStore.success('FACILITY_INVENTORY.DELETE.SUCCESS');
					await this.queryUtility.fetch();
				} catch (err) {
					notificationStore.error('LIST_LAYOUT.ERROR_DELETE', err);
				}
			}
		);
	}

	@action.bound
	async editResource(item) {
		this.isCreate = false;
		if (!this.isDone) {
			this.rootStore.routerStore.goTo(
				'master.app.main.facility-inventory.facility-inventory-detail.edit',
				{ id: item.id, facilityInventoryCountHeaderId: this.facilityInventoryCountHeaderId }
			);
		}
	}

	@action.bound
	isQtyValid(value, propertyName) {
		if (parseFloat(value).toFixed(2) > 999.99 && (propertyName === 'quantity' || propertyName === 'caseQty' || propertyName === 'eachQty')) {
			return false;
		}
		return true;
	}

	@action.bound
	editItem(index, value, propertyName) {
		if (!this.isQtyValid(value, propertyName)) {
			return;
		}

		this.facilityInventoryCountDetails[index][propertyName] = value;
	}

	@action.bound
	async updateQuantity(id, index, value, propertyName) {
		if (!this.isQtyValid(value, propertyName)) {
			return;
		}

		if (value == null || value == undefined || value === "")
			value = 0;

		if (value.toString() === this.oldQuantity[index][propertyName].toString()) {
			return;
		}
		else {
			if (this.facilityInventoryCountDetails[index]["qtyPerCase"] === 0) {
				if (propertyName === "caseQty") {
					this.oldQuantity[index]["caseQty"] = 0;
					this.oldQuantity[index]["quantity"] = this.oldQuantity[index]["eachQty"];
				}
				else {
					this.oldQuantity[index]["eachQty"] = value;
					this.oldQuantity[index]["quantity"] = this.oldQuantity[index]["eachQty"];
				}
			}
			else {
				if (propertyName === "eachQty" && value >= this.facilityInventoryCountDetails[index]["qtyPerCase"]) {
					this.oldQuantity[index]["caseQty"] += Math.floor(value / this.facilityInventoryCountDetails[index]["qtyPerCase"]);
					value = value % this.facilityInventoryCountDetails[index]["qtyPerCase"];
				}
				this.oldQuantity[index][propertyName] = value;
				this.oldQuantity[index]["quantity"] = (this.facilityInventoryCountDetails[index]["qtyPerCase"] * this.oldQuantity[index]["caseQty"]) + this.oldQuantity[index]["eachQty"];
			}
		}

		await this.facilityInventoryCountDetailService.updateQuantity({
			id: id,
			quantity: this.oldQuantity[index]["quantity"]
		});

		this.facilityInventoryCountDetails[index]["caseQty"] = this.oldQuantity[index]["caseQty"];
		this.facilityInventoryCountDetails[index]["eachQty"] = this.oldQuantity[index]["eachQty"];
		this.facilityInventoryCountDetails[index]["quantity"] = this.oldQuantity[index]["quantity"];

		if (this.facilityInventoryCountDetails[index]["qtyPerCase"] === 0 && propertyName === "caseQty") {
			this.rootStore.notificationStore.error("Case Qty cannot be entered for a product that doesn't have its case version.");
		}
		else {
			this.rootStore.notificationStore.success("Quantity successfully updated.");
		}
	}

	@action.bound
	goToFacilityInventoryCountImport() {
		this.rootStore.routerStore.goTo(
			"master.app.main.facility-inventory.file-upload",
			{
				facilityInventoryCountHeaderId: this.facilityInventoryCountHeaderId
			}
		);
	}

	@action.bound
	async convertCaseToEachItems() {
		try {
			await this.facilityInventoryCountDetailService.convert(this.facilityInventoryCountHeaderId);
			await this.queryUtility.fetch();
			this.rootStore.notificationStore.success("Convert successfully done.");
		} catch (err) {
			this.rootStore.notificationStore.success("Error occurred while trying to convert.");
		}
	}

	@action.bound
	async openEditModal() {
		const id = this.rootStore.routerStore.routerState.params.facilityInventoryCountHeaderId;
		this.editModal.open({
			id: id,
			facilityOrder: this.facilityOrder,
			title: "Edit Count Date"
		});
	}
}

export default FacilityInventoryCountDetailListViewStore;